import { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { z } from 'zod';

import { useRestApi } from '../../../../restApiInstance.js';
import { PulseMetric } from '../../../../sdks/tableau/types/pulse.js';
import { WebMcpServer } from '../../../../server.web.js';
import { WebTool } from '../../tool.js';
import { constrainPulseMetrics } from '../constrainPulseMetrics.js';

const paramsSchema = {
  pulseMetricDefinitionID: z.string().length(36),
};

export const getListPulseMetricsFromMetricDefinitionIdTool = (
  server: WebMcpServer,
): WebTool<typeof paramsSchema> => {
  const listPulseMetricsFromMetricDefinitionIdTool = new WebTool({
    server,
    name: 'list-pulse-metrics-from-metric-definition-id',
    description: `
Retrieves a list of published Pulse Metrics from a Pulse Metric Definition using the Tableau REST API.  Use this tool when a user requests to list Tableau Pulse Metrics for a specific Pulse Metric Definition on the current site.

**Parameters:**
- \`pulseMetricDefinitionID\` (required): The ID of the Pulse Metric Definition to list metrics for.  It should be the ID of the Pulse Metric Definition, not the name.  Example: BBC908D8-29ED-48AB-A78E-ACF8A424C8C3

**Example Usage:**
- List all Pulse Metrics for this Pulse Metric Definition
`,
    paramsSchema,
    annotations: {
      title: 'List Pulse Metrics from Metric Definition ID',
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
    callback: async ({ pulseMetricDefinitionID }, extra): Promise<CallToolResult> => {
      return await listPulseMetricsFromMetricDefinitionIdTool.logAndExecute<Array<PulseMetric>>({
        extra,
        args: { pulseMetricDefinitionID },
        callback: async () => {
          return await useRestApi({
            ...extra,
            jwtScopes: listPulseMetricsFromMetricDefinitionIdTool.requiredApiScopes,
            callback: async (restApi) => {
              return await restApi.pulseMethods.listPulseMetricsFromMetricDefinitionId(
                pulseMetricDefinitionID,
              );
            },
          });
        },
        constrainSuccessResult: async (metrics) => {
          const configWithOverrides = await extra.getConfigWithOverrides();

          return constrainPulseMetrics({
            metrics,
            boundedContext: configWithOverrides.boundedContext,
          });
        },
      });
    },
  });

  return listPulseMetricsFromMetricDefinitionIdTool;
};
